import { Injectable } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { map, Observable, of, ReplaySubject, switchMap, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
  private _notificationsState: Notification[] = [];

  constructor() {
    this._notifications.next(this._notificationsState);
  }

  get notifications$(): Observable<Notification[]> {
    return this._notifications.asObservable();
  }

  /**
   * Get all notifications
   * Safe placeholder mode: returns empty list until real BE is wired.
   */
  getAll(): Observable<Notification[]> {
    this._notifications.next(this._notificationsState);
    return of(this._notificationsState);
  }

  /**
   * Create a notification locally
   */
  create(notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      map((notifications) => {
        const newNotification: Notification = {
          ...notification,
          id: notification.id || crypto.randomUUID(),
        };

        const next = [newNotification, ...notifications];
        this._notificationsState = next;
        this._notifications.next(next);

        return newNotification;
      }),
    );
  }

  /**
   * Update a notification locally
   */
  update(id: string, notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      map((notifications) => {
        const index = notifications.findIndex((item) => item.id === id);

        if (index === -1) {
          return notification;
        }

        const next = [...notifications];
        next[index] = { ...notification, id };

        this._notificationsState = next;
        this._notifications.next(next);

        return next[index];
      }),
    );
  }

  /**
   * Delete a notification locally
   */
  delete(id: string): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      map((notifications) => {
        const next = notifications.filter((item) => item.id !== id);

        this._notificationsState = next;
        this._notifications.next(next);

        return true;
      }),
    );
  }

  /**
   * Mark all notifications as read locally
   */
  markAllAsRead(): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      map((notifications) => {
        const next = notifications.map((notification) => ({
          ...notification,
          read: true,
        }));

        this._notificationsState = next;
        this._notifications.next(next);

        return true;
      }),
    );
  }
}
